// Settings modal — single source of truth used both from the home menu's
// SETTINGS entry and the in-match pause menu (PauseController). Same
// modal, same tabs, same styling everywhere.
//
// Visual language is lifted from the online-match lobby (PartyPanel.ts):
// pale-blue gradient body, white pill rows with cyan labels and dark
// values, primary-blue gradient for active states, primary-orange for
// destructive. The card itself is a 1:1 design-pixel artefact wrapped in
// the shared StageScaler so it scales uniformly to fit the viewport
// (mobile landscape no longer hides the close button).
//
// Tabs (left → right): Gameplay, Graphics, Audio, Controls.

import { CONFIG } from '../config';
import { MenuNav, type MenuItem } from './MenuNav';
import { menuAudio } from './MenuAudio';
import { StageScaler } from './StageScaler';
import { showConfirmationPopup } from './components/ConfirmationPopup';
import { detectControllerVendor, faceLabelsFor, type ControllerVendor } from '../core/GamepadVendor';
import {
  type GraphicsSettings,
  type RenderScale,
  type ShadowQuality,
  type GraphicsPresetId,
  GRAPHICS_PRESETS,
  GRAPHICS_PRESET_ORDER,
  findActivePreset,
  loadGraphicsSettings,
  saveGraphicsSettings,
  applyGraphicsSettingsToConfig
} from './graphicsSettings';
import {
  type AudioSettings,
  loadAudioSettings,
  saveAudioSettings,
  applyAudioSettings
} from './audioSettings';
import {
  type ControlsSettings,
  SENSITIVITY_RANGE,
  loadControlsSettings,
  saveControlsSettings,
  applyControlsSettings
} from './controlsSettings';
import { resetTutorialState } from './tutorialState';
import { SUPPORTED_LANGUAGES, getLocale, setLocale, t } from '../i18n';
import { isFullscreen, enterFullscreen, exitFullscreen, onFullscreenChange } from './fullscreen';
import { accountState } from '../profile/account';
import { loadToken } from '../profile/accountStore';
import { showNamePickerModal } from './NamePickerModal';
import { ApiError } from '../net/accountApi';

type TabId = 'gameplay' | 'graphics' | 'audio' | 'controls';

const TABS: { id: TabId; labelKey: string }[] = [
  { id: 'gameplay', labelKey: 'settings.gameplay' },
  { id: 'graphics', labelKey: 'settings.graphics' },
  { id: 'audio',    labelKey: 'settings.audio' },
  { id: 'controls', labelKey: 'settings.controls' }
];

/** Card design size — the StageScaler scales the whole card uniformly to
 *  fit the viewport. Picked so the bindings two-column table reads
 *  comfortably without forcing a tiny scale on landscape mobile. */
const CARD_W = 1100;
const CARD_H = 760;

export interface SettingsModalOptions {
  onClose: () => void;
  /** Where the modal was opened from. 'pause' enables Skip Tutorial when
   *  isTutorial+tutorialFlow are also set, and skips the home-only
   *  pointer-lock release. Defaults to 'home'. */
  context?: 'home' | 'pause';
  /** Whether the active match is the tutorial. Only meaningful in
   *  context='pause'. Defaults to false. */
  isTutorial?: boolean;
  /** Active tutorial flow handle, if context='pause' && isTutorial.
   *  Activating Skip Tutorial calls requestSkip(); the modal then closes
   *  itself so the tutorial flow's done-promise can resolve. */
  tutorialFlow?: { requestSkip: () => void } | null;
  /** Optional Leave Game hook — when present, the Gameplay tab renders
   *  an orange Leave Game action that confirms then calls this. Only
   *  effective in context='pause'. The local match runner provides this;
   *  online play has its own leave flow via the existing party / lobby
   *  paths. */
  onLeaveMatch?: () => void;
}

export interface SettingsModalHandle {
  dismiss(): void;
}

const STYLES_ID = 'settings-modal-styles';

export function showSettingsModal(opts: SettingsModalOptions): SettingsModalHandle {
  ensureStyles();

  const context = opts.context ?? 'home';
  const isTutorial = opts.isTutorial ?? false;
  const tutorialFlow = opts.tutorialFlow ?? null;

  // Live-loaded copies of every persisted settings blob. Each tab mutates
  // its own struct + persists; the modal holds the structs in the closure
  // so re-rendering a tab doesn't lose unsaved changes.
  const graphics = loadGraphicsSettings();
  const audio = loadAudioSettings();
  const controls = loadControlsSettings();

  let activeTab: TabId = 'gameplay';
  let nav: MenuNav | null = null;
  let dismissed = false;
  let detectedVendor: ControllerVendor = detectControllerVendor();
  // Sub-panel slot — gameplay tab uses it for the language picker and the
  // account / token management screen. Rebuild() routes the body render
  // through it.
  let subPanel: 'language' | 'account' | null = null;

  // Home-only: drop pointer lock so the user can interact. In pause
  // context, the canvas is already not pointer-locked (PauseController
  // takes care of that on open) and re-asserting here would race.
  if (context === 'home') releasePointerLock();

  const root = document.createElement('div');
  root.className = 'settings-modal';
  root.dataset.context = context;
  root.innerHTML = `
    <div class="settings-modal-backdrop"></div>
    <div class="settings-modal-stage-host">
      <div class="settings-modal-card">
        <div class="settings-modal-header">
          <div class="header-title">${escapeHtml(t('settings.title'))}</div>
          <button type="button" class="close-btn" aria-label="${escapeHtml(t('settings.close'))}">×</button>
        </div>
        <div class="settings-modal-tabs"></div>
        <div class="settings-modal-body"></div>
      </div>
    </div>
  `;
  document.body.appendChild(root);

  const cardEl = root.querySelector('.settings-modal-card') as HTMLElement;
  const tabsEl = root.querySelector('.settings-modal-tabs') as HTMLElement;
  const bodyEl = root.querySelector('.settings-modal-body') as HTMLElement;
  const closeBtn = root.querySelector('.close-btn') as HTMLButtonElement;

  // Card design size = 1:1 design pixels. StageScaler centers + scales
  // the card to fit the viewport. The scrim behind stays full-screen.
  cardEl.style.width = `${CARD_W}px`;
  cardEl.style.height = `${CARD_H}px`;
  const scaler = new StageScaler({
    stageEl: cardEl,
    designW: CARD_W,
    designH: CARD_H,
    // Don't blow the card up past 1× on a huge monitor — design-size 1100×760
    // is already large enough that 1.0 reads comfortably.
    maxScale: 1.0
  });
  scaler.start();

  for (const tab of TABS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tab-btn';
    btn.dataset.tab = tab.id;
    btn.textContent = t(tab.labelKey);
    tabsEl.appendChild(btn);
  }

  function rebuild(): void {
    nav?.dispose();
    bodyEl.innerHTML = '';

    for (const tabBtn of Array.from(tabsEl.querySelectorAll<HTMLButtonElement>('.tab-btn'))) {
      tabBtn.classList.toggle('active', tabBtn.dataset.tab === activeTab);
    }

    const items: MenuItem[] = [];

    let col = 0;
    let activeTabCol = 0;
    for (const tabBtn of Array.from(tabsEl.querySelectorAll<HTMLButtonElement>('.tab-btn'))) {
      const id = tabBtn.dataset.tab as TabId;
      if (id === activeTab) activeTabCol = col;
      const myCol = col++;
      items.push({
        el: tabBtn,
        row: 0, col: myCol,
        onActivate: () => {
          if (activeTab === id) return;
          activeTab = id;
          rebuild();
        }
      });
    }
    items.push({
      el: closeBtn,
      row: 0, col: col++,
      onActivate: () => dismiss()
    });

    if (activeTab === 'gameplay') {
      if (subPanel === 'language') {
        buildLanguageSubPanel(bodyEl, items, {
          onPick: (code) => {
            setLocale(code);
            subPanel = null;
            // Re-render the whole modal so every label re-translates
            // (tabs, header, segmented values, presets, quips). The
            // gameplay tab is restored as the active tab.
            rebuildAll();
          },
          onBack: () => {
            subPanel = null;
            rebuild();
          }
        });
      } else if (subPanel === 'account') {
        buildAccountSubPanel(bodyEl, items, {
          onBack: () => { subPanel = null; rebuild(); },
          onChanged: () => rebuild(),
        });
      } else {
        buildGameplayBody(bodyEl, controls, items, {
          showSkipTutorial: context === 'pause' && isTutorial && tutorialFlow !== null,
          showLeaveMatch: context === 'pause' && opts.onLeaveMatch !== undefined,
          onResetTutorial: () => withNavSuspended(() => confirmAndResetTutorial()),
          onSkipTutorial: () => withNavSuspended(() => {
            if (tutorialFlow) confirmAndSkipTutorial(tutorialFlow, () => dismiss());
          }),
          onLeaveMatch: () => withNavSuspended(() => {
            if (opts.onLeaveMatch) confirmAndLeaveMatch(opts.onLeaveMatch, () => dismiss());
          }),
          onOpenLanguage: () => {
            subPanel = 'language';
            rebuild();
          },
          onOpenAccount: () => {
            subPanel = 'account';
            rebuild();
          },
        });
      }
    } else if (activeTab === 'graphics') {
      buildGraphicsBody(bodyEl, graphics, items);
    } else if (activeTab === 'audio') {
      buildAudioBody(bodyEl, audio, items);
    } else if (activeTab === 'controls') {
      buildControlsBody(bodyEl, detectedVendor);
    }

    nav = new MenuNav({
      items,
      initialIndex: activeTabCol,
      // Sub-panel context: B / cancel goes back to the gameplay tab
      // instead of dismissing the whole modal — matches the chevron-Back
      // button at the top of the sub-panel.
      onCancel: subPanel
        ? () => { subPanel = null; rebuild(); }
        : () => dismiss()
    });
    nav.mount();
  }

  /** Re-translate every visible label by re-rendering the modal from
   *  scratch. Called after a language change so tab names, header
   *  title, body labels, quips, etc. all flip in one shot. */
  function rebuildAll(): void {
    // Re-render tab labels — tabsEl was populated once with t() values
    // that won't auto-update.
    const tabBtns = Array.from(tabsEl.querySelectorAll<HTMLButtonElement>('.tab-btn'));
    for (const btn of tabBtns) {
      const id = btn.dataset.tab as TabId;
      const def = TABS.find((t) => t.id === id);
      if (def) btn.textContent = t(def.labelKey);
    }
    // Re-render the header title + close-btn aria.
    const titleEl = root.querySelector('.header-title') as HTMLElement | null;
    if (titleEl) titleEl.textContent = t('settings.title');
    closeBtn.setAttribute('aria-label', t('settings.close'));
    rebuild();
  }

  function onResize(): void {
    if (activeTab === 'graphics') updateResolutionLabels(bodyEl);
  }
  addEventListener('resize', onResize);

  // Live re-render of the controls bindings tab when a controller is
  // plugged in / out — face glyphs flip Xbox ↔ PS without the user
  // closing and reopening the modal.
  function onGamepadChange(): void {
    const v = detectControllerVendor();
    if (v === detectedVendor) return;
    detectedVendor = v;
    if (activeTab === 'controls') rebuild();
  }
  addEventListener('gamepadconnected', onGamepadChange);
  addEventListener('gamepaddisconnected', onGamepadChange);

  // Fullscreen state is browser-owned (Esc to exit, F11 to toggle, OS
  // chrome). Mirror it into the Display row whenever it changes so the
  // segments always show the live state without relying on local cache.
  const detachFullscreen = onFullscreenChange(() => {
    if (activeTab === 'graphics') refreshDisplayRow(bodyEl);
  });

  function onKeyDown(e: KeyboardEvent): void {
    if (dismissed) return;
    if (e.code === 'Escape' || e.code === 'Backspace') {
      e.preventDefault();
      e.stopPropagation();
      // Inside any sub-panel, Esc goes back to the gameplay tab —
      // same end state as pressing the chevron-Back button or B on a
      // controller.
      if (subPanel) {
        subPanel = null;
        rebuild();
      } else {
        dismiss();
      }
    }
  }
  document.addEventListener('keydown', onKeyDown, { capture: true });

  function dismiss(): void {
    if (dismissed) return;
    dismissed = true;
    nav?.dispose();
    nav = null;
    scaler.stop();
    removeEventListener('resize', onResize);
    removeEventListener('gamepadconnected', onGamepadChange);
    removeEventListener('gamepaddisconnected', onGamepadChange);
    detachFullscreen();
    document.removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
    root.classList.add('closing');
    setTimeout(() => root.remove(), 280);
    opts.onClose();
  }

  /** Suspend the modal's keyboard / gamepad nav while a ConfirmationPopup
   *  is on top, so the same A press doesn't activate both the popup and
   *  the row underneath. Re-enables once the popup-driven action returns
   *  (synchronously — the popup mounts inside the callback and detaches
   *  its own listeners on dismiss). */
  function withNavSuspended(fn: () => void): void {
    nav?.setEnabled(false);
    fn();
    // The popup's MenuNav becomes the active surface; its Cancel/Confirm
    // both eventually fire (sync mount, async dismiss). Listen for the
    // popup's own dismiss by polling `document.querySelector('.confirm-popup')`
    // — when it's gone, re-enable. Simpler than threading a callback
    // through every confirm helper.
    const reenable = (): void => {
      if (document.querySelector('.confirm-popup')) {
        requestAnimationFrame(reenable);
        return;
      }
      if (!dismissed) nav?.setEnabled(true);
    };
    requestAnimationFrame(reenable);
  }

  rebuild();
  // Trigger entry animation on the next frame so the transition fires.
  requestAnimationFrame(() => root.classList.add('shown'));

  return { dismiss };
}

// ---------------------------------------------------------------------------
// Tab body builders

interface GameplayCallbacks {
  showSkipTutorial: boolean;
  showLeaveMatch: boolean;
  onResetTutorial: () => void;
  onSkipTutorial: () => void;
  onLeaveMatch: () => void;
  onOpenLanguage: () => void;
  onOpenAccount: () => void;
}

function buildGameplayBody(
  bodyEl: HTMLElement,
  settings: ControlsSettings,
  items: MenuItem[],
  cb: GameplayCallbacks
): void {
  // Order: sens / invert (always) → Language → Leave Game (pause only,
  // destructive) → Skip Tutorial (pause+tutorial only, destructive) →
  // Reset Tutorial (always, primary blue — less jarring next to Leave /
  // Skip).
  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === getLocale())
    ?? SUPPORTED_LANGUAGES[0]!;
  bodyEl.innerHTML = `
    <div class="rows">
      ${renderSliderRow('mouseSensitivity', t('settings.mouseSensitivity'),
        settings.mouseSensitivity, SENSITIVITY_RANGE.min, SENSITIVITY_RANGE.max, 0.05,
        formatSensitivity(settings.mouseSensitivity))}
      ${renderToggleRow('invertY', t('settings.invertY'), settings.invertY)}
      <button type="button" class="row language-row" data-action="language">
        <span class="row-label">${escapeHtml(t('settings.language'))}</span>
        <span class="language-current">
          <span class="language-flag" aria-hidden="true">${currentLang.flag}</span>
          <span class="language-name">${escapeHtml(currentLang.name)}</span>
          <span class="language-chevron" aria-hidden="true">›</span>
        </span>
      </button>
      <button type="button" class="row language-row" data-action="account">
        <span class="row-label">${escapeHtml(t('account.title'))}</span>
        <span class="language-current">
          <span class="language-name">${escapeHtml(accountState.get()?.name ?? '—')}</span>
          <span class="language-chevron" aria-hidden="true">›</span>
        </span>
      </button>
      ${cb.showLeaveMatch
        ? `<button type="button" class="action-row destructive" data-action="leaveMatch">
             <span class="action-label">${escapeHtml(t('settings.leaveMatch'))}</span>
           </button>`
        : ''}
      ${cb.showSkipTutorial
        ? `<button type="button" class="action-row destructive" data-action="skipTutorial">
             <span class="action-label">${escapeHtml(t('settings.skipTutorial'))}</span>
           </button>`
        : ''}
      ${cb.showSkipTutorial
        ? '' /* In tutorial: Skip is the only relevant action; "Reset"
                offers to replay something we're already replaying. */
        : `<button type="button" class="action-row primary" data-action="resetTutorial">
             <span class="action-label">${escapeHtml(t('settings.resetTutorial'))}</span>
           </button>`}
    </div>
  `;

  let row = 1;

  // Mouse sensitivity slider.
  const sensRow = bodyEl.querySelector('[data-row="mouseSensitivity"]') as HTMLElement;
  const sensInput = sensRow.querySelector('input[type=range]') as HTMLInputElement;
  const sensValue = sensRow.querySelector('.slider-value') as HTMLElement;
  sensInput.addEventListener('input', () => {
    const v = parseFloat(sensInput.value);
    settings.mouseSensitivity = v;
    sensValue.textContent = formatSensitivity(v);
    applyControlsSettings(settings);
  });
  sensInput.addEventListener('change', () => saveControlsSettings(settings));
  items.push({
    el: sensRow,
    row: row++, col: 0,
    onFocus: () => sensInput.focus(),
    onActivate: () => sensInput.focus(),
    onAdjust: (delta) => {
      // Bigger step for the sensitivity slider since 0.05 increments would
      // feel sluggish with controller dpad. 0.1× per press = 15 presses
      // covers the full 0.5..2.0 range.
      adjustSlider(sensInput, delta, 0.1);
      saveControlsSettings(settings);
    }
  });

  // Invert Y toggle (segmented).
  const invertRow = bodyEl.querySelector('[data-row="invertY"]') as HTMLElement;
  const invertSegs = invertRow.querySelectorAll<HTMLButtonElement>('.seg');
  let col = 0;
  invertSegs.forEach((seg) => {
    const v = seg.dataset.value === 'on';
    items.push({
      el: seg,
      row, col: col++,
      onActivate: () => {
        settings.invertY = v;
        markActive(seg);
        applyControlsSettings(settings);
        saveControlsSettings(settings);
      }
    });
  });
  row++;

  // Language — opens the flag-grid sub-panel.
  const langRow = bodyEl.querySelector('[data-action="language"]') as HTMLButtonElement;
  items.push({
    el: langRow,
    row: row++, col: 0,
    onActivate: () => cb.onOpenLanguage()
  });

  // Account — opens the soft-auth sub-panel (rename / token).
  const accRow = bodyEl.querySelector('[data-action="account"]') as HTMLButtonElement;
  items.push({
    el: accRow,
    row: row++, col: 0,
    onActivate: () => cb.onOpenAccount()
  });

  // Leave Game — only in pause context, with a destructive confirm.
  if (cb.showLeaveMatch) {
    const leaveBtn = bodyEl.querySelector('[data-action="leaveMatch"]') as HTMLButtonElement;
    items.push({
      el: leaveBtn,
      row: row++, col: 0,
      onActivate: () => cb.onLeaveMatch()
    });
  }

  // Skip Tutorial — only when in pause context on the tutorial map.
  if (cb.showSkipTutorial) {
    const skipBtn = bodyEl.querySelector('[data-action="skipTutorial"]') as HTMLButtonElement;
    items.push({
      el: skipBtn,
      row: row++, col: 0,
      onActivate: () => cb.onSkipTutorial()
    });
  }

  // Reset Tutorial — primary-blue so it doesn't read as destructive next
  // to Leave Game / Skip Tutorial. Hidden while the tutorial is actively
  // running (showSkipTutorial implies that), where "Reset" offers to
  // replay something we're already in the middle of replaying.
  if (!cb.showSkipTutorial) {
    const resetBtn = bodyEl.querySelector('[data-action="resetTutorial"]') as HTMLButtonElement;
    items.push({
      el: resetBtn,
      row: row++, col: 0,
      onActivate: () => cb.onResetTutorial()
    });
  }
}

interface LanguageCallbacks {
  onPick: (code: string) => void;
  onBack: () => void;
}

function buildLanguageSubPanel(
  bodyEl: HTMLElement,
  items: MenuItem[],
  cb: LanguageCallbacks
): void {
  const current = getLocale();
  const flags = SUPPORTED_LANGUAGES.map((l) => `
    <button type="button" class="lang-flag${l.code === current ? ' selected' : ''}" data-code="${escapeAttr(l.code)}">
      <span class="lang-flag-emoji" aria-hidden="true">${l.flag}</span>
      <span class="lang-flag-name">${escapeHtml(l.name)}</span>
    </button>
  `).join('');
  bodyEl.innerHTML = `
    <div class="lang-subpanel">
      <button type="button" class="lang-back" data-action="back">
        <span class="lang-back-chevron" aria-hidden="true">‹</span>
        <span>${escapeHtml(t('settings.languageBack'))}</span>
      </button>
      <div class="lang-grid" role="listbox">${flags}</div>
    </div>
  `;

  // Back button — row 1 so the flag list (row 2..N) sits below it in
  // the nav grid. Default focus lands on Back so a quick B-press feels
  // natural; pressing Down from Back lands on the currently-selected
  // language (closest column 0 row 2 entry).
  const backBtn = bodyEl.querySelector('[data-action="back"]') as HTMLButtonElement;
  items.push({
    el: backBtn,
    row: 1, col: 0,
    onActivate: () => cb.onBack()
  });

  const flagBtns = Array.from(bodyEl.querySelectorAll<HTMLButtonElement>('.lang-flag'));
  flagBtns.forEach((btn, i) => {
    items.push({
      el: btn,
      row: 2 + i,
      col: 0,
      onActivate: () => cb.onPick(btn.dataset.code!),
      onFocus: () => btn.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    });
  });

  // Center the currently-selected flag on mount so a return visit shows
  // the active language at a glance even when it's deep in the list.
  const selected = flagBtns.find((b) => b.dataset.code === current);
  if (selected) selected.scrollIntoView({ block: 'center', behavior: 'auto' });
}

function escapeAttr(s: string): string {
  return s.replace(/["<>&]/g, (c) => ({ '"': '&quot;', '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c]!);
}

interface AccountCallbacks {
  onBack: () => void;
  /** Notifies the parent so it can rebuild the panel after a rename or
   *  token change. We don't fully re-render in-place because the rename
   *  flow opens its own modal stacked over the settings card. */
  onChanged: () => void;
}

function buildAccountSubPanel(
  bodyEl: HTMLElement,
  items: MenuItem[],
  cb: AccountCallbacks
): void {
  const account = accountState.get();
  const token = loadToken() ?? '';
  bodyEl.innerHTML = `
    <div class="lang-subpanel account-subpanel">
      <button type="button" class="lang-back" data-action="back">
        <span class="lang-back-chevron" aria-hidden="true">‹</span>
        <span>${escapeHtml(t('settings.languageBack'))}</span>
      </button>
      <div class="acc-rows">
        <div class="row">
          <span class="row-label">${escapeHtml(t('account.nameLabel'))}</span>
          <div class="acc-name-stack">
            <span class="acc-name">${escapeHtml(account?.name ?? '—')}</span>
            <button type="button" class="action-row primary acc-rename" data-action="rename">
              <span class="action-label">${escapeHtml(t('account.changeName'))}</span>
            </button>
          </div>
        </div>
        <div class="row acc-token-row">
          <span class="row-label">${escapeHtml(t('account.tokenLabel'))}</span>
          <div class="acc-token-stack">
            <div class="acc-token-blurb">${escapeHtml(t('account.tokenBlurb'))}</div>
            <input type="text" class="acc-token-input" readonly value="${escapeAttr(token)}" />
            <div class="acc-token-actions">
              <button type="button" class="action-row primary acc-toggle" data-action="toggle">
                <span class="action-label">${escapeHtml(t('account.show'))}</span>
              </button>
              <button type="button" class="action-row primary acc-copy" data-action="copy">
                <span class="action-label">${escapeHtml(t('account.copy'))}</span>
              </button>
              <button type="button" class="action-row destructive acc-rotate" data-action="rotate">
                <span class="action-label">${escapeHtml(t('account.rotate'))}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="row acc-import-row">
          <span class="row-label">${escapeHtml(t('account.import'))}</span>
          <div class="acc-token-stack">
            <div class="acc-token-blurb">${escapeHtml(t('account.importBlurb'))}</div>
            <input type="text" class="acc-import-input" placeholder="paste token" />
            <button type="button" class="action-row primary acc-apply" data-action="apply">
              <span class="action-label">${escapeHtml(t('account.importApply'))}</span>
            </button>
            <div class="acc-import-status" data-state="idle"></div>
          </div>
        </div>
      </div>
    </div>
  `;

  // Mask the token by default — the input shows dots until the user
  // explicitly reveals it. Reveal flips the input type and the toggle label.
  const tokenInput = bodyEl.querySelector('.acc-token-input') as HTMLInputElement;
  tokenInput.type = 'password';
  const toggleBtn = bodyEl.querySelector('.acc-toggle') as HTMLButtonElement;
  const toggleLabel = toggleBtn.querySelector('.action-label') as HTMLElement;
  let revealed = false;
  const setReveal = (on: boolean) => {
    revealed = on;
    tokenInput.type = on ? 'text' : 'password';
    toggleLabel.textContent = t(on ? 'account.hide' : 'account.show');
  };

  const backBtn = bodyEl.querySelector('[data-action="back"]') as HTMLButtonElement;
  items.push({ el: backBtn, row: 1, col: 0, onActivate: () => cb.onBack() });

  // Rename — opens NamePickerModal in rename mode. The modal sits above
  // the settings card; on success we refresh by calling onChanged.
  const renameBtn = bodyEl.querySelector('[data-action="rename"]') as HTMLButtonElement;
  items.push({
    el: renameBtn,
    row: 2, col: 0,
    onActivate: () => {
      showNamePickerModal({
        mode: 'rename',
        initialName: account?.name,
        onSubmit: async (name) => {
          await accountState.rename(name);
          cb.onChanged();
        },
        onCancel: () => { /* keep settings open */ },
      });
    },
  });

  items.push({
    el: toggleBtn,
    row: 3, col: 0,
    onActivate: () => setReveal(!revealed),
  });

  const copyBtn = bodyEl.querySelector('.acc-copy') as HTMLButtonElement;
  const copyLabel = copyBtn.querySelector('.action-label') as HTMLElement;
  let copyResetTimer = 0;
  items.push({
    el: copyBtn,
    row: 3, col: 1,
    onActivate: async () => {
      try {
        await navigator.clipboard.writeText(loadToken() ?? '');
        copyLabel.textContent = t('account.copied');
        window.clearTimeout(copyResetTimer);
        copyResetTimer = window.setTimeout(() => {
          copyLabel.textContent = t('account.copy');
        }, 1500);
      } catch {
        // Clipboard API may be unavailable (insecure context, no permission);
        // fall back to selecting the input so the user can copy manually.
        setReveal(true);
        tokenInput.focus();
        tokenInput.select();
      }
    },
  });

  const rotateBtn = bodyEl.querySelector('.acc-rotate') as HTMLButtonElement;
  items.push({
    el: rotateBtn,
    row: 3, col: 2,
    onActivate: () => {
      // Inline confirm — clipboard the user away if they tap by accident.
      // Reuses the same confirmation popup pattern as Reset Tutorial.
      showConfirmationPopup({
        title: t('account.rotateConfirmTitle'),
        body: t('account.rotateConfirmBody'),
        confirmLabel: t('account.rotateConfirmAction'),
        destructive: true,
        onConfirm: () => {
          void (async () => {
            try {
              await accountState.rotateToken();
              cb.onChanged();
            } catch (e) {
              console.warn('[settings] rotate-token failed', e);
            }
          })();
        },
      });
    },
  });

  // Import — paste a token, validate against the server, swap to that
  // account if it resolves.
  const importInput = bodyEl.querySelector('.acc-import-input') as HTMLInputElement;
  const applyBtn = bodyEl.querySelector('.acc-apply') as HTMLButtonElement;
  const importStatus = bodyEl.querySelector('.acc-import-status') as HTMLElement;
  let importing = false;
  const doImport = async () => {
    if (importing) return;
    const v = importInput.value.trim();
    if (v.length < 8) return;
    importing = true;
    importStatus.dataset.state = 'checking';
    importStatus.textContent = t('name.statusChecking');
    try {
      await accountState.importToken(v);
      importing = false;
      cb.onChanged();
    } catch (e) {
      importing = false;
      importStatus.dataset.state = 'error';
      importStatus.textContent = e instanceof ApiError && e.status === 404
        ? t('account.importError')
        : t('name.statusNetworkError');
    }
  };
  applyBtn.addEventListener('click', () => void doImport());
  importInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void doImport();
    }
  });
  items.push({ el: applyBtn, row: 4, col: 0, onActivate: () => void doImport() });
}

function buildAudioBody(
  bodyEl: HTMLElement,
  settings: AudioSettings,
  items: MenuItem[]
): void {
  bodyEl.innerHTML = `
    <div class="rows">
      ${renderSliderRow('master', t('settings.master'), settings.master, 0, 1, 0.01, formatPercent(settings.master))}
      ${renderSliderRow('music',  t('settings.music'),  settings.music,  0, 1, 0.01, formatPercent(settings.music))}
      ${renderSliderRow('sfx',    t('settings.sfx'),    settings.sfx,    0, 1, 0.01, formatPercent(settings.sfx))}
    </div>
  `;

  const wireSlider = (key: 'master' | 'music' | 'sfx', row: number): void => {
    const r = bodyEl.querySelector(`[data-row="${key}"]`) as HTMLElement;
    const input = r.querySelector('input[type=range]') as HTMLInputElement;
    const value = r.querySelector('.slider-value') as HTMLElement;
    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      settings[key] = v;
      value.textContent = formatPercent(v);
      applyAudioSettings(settings);
    });
    input.addEventListener('change', () => saveAudioSettings(settings));
    items.push({
      el: r,
      row, col: 0,
      onFocus: () => input.focus(),
      onActivate: () => input.focus(),
      // Volume sliders adjust in 5% steps — feels snappy on dpad and
      // covers the full 0..100% range in 20 presses.
      onAdjust: (delta) => {
        adjustSlider(input, delta, 0.05);
        saveAudioSettings(settings);
      }
    });
  };

  wireSlider('master', 1);
  wireSlider('music', 2);
  wireSlider('sfx', 3);
}

function buildControlsBody(bodyEl: HTMLElement, vendor: ControllerVendor): void {
  const face = faceLabelsFor(vendor);
  const vendorLabel = vendor === 'playstation'
    ? t('settings.controllerPS')
    : t('settings.controllerXbox');

  // Bindings come straight from Input.ts / Gamepad.ts. Two columns,
  // read-only — rebinding is out of scope for v1.
  const rows = [
    { action: t('bindings.move'),     kbm: 'WASD',          gp: t('bindings.leftStick') },
    { action: t('bindings.aim'),      kbm: t('bindings.mouse'), gp: t('bindings.rightStick') },
    { action: t('bindings.punch'),    kbm: t('bindings.lmb'),   gp: face.left },
    { action: t('bindings.grab'),     kbm: 'E',                 gp: 'RT' },
    { action: t('bindings.jump'),     kbm: t('bindings.space'), gp: face.bottom },
    { action: t('bindings.sprint'),   kbm: t('bindings.shift'), gp: 'LT' },
    { action: t('bindings.jumpKick'), kbm: t('bindings.rmb'),   gp: face.right },
    { action: t('bindings.throw'),    kbm: 'Q',                 gp: '—' },
    { action: t('bindings.pause'),    kbm: 'P',                 gp: t('bindings.start') }
  ];

  const rowMarkup = rows.map((r) => `
    <tr>
      <td class="action-cell">${escapeHtml(r.action)}</td>
      <td class="binding-cell">${escapeHtml(r.kbm)}</td>
      <td class="binding-cell">${escapeHtml(r.gp)}</td>
    </tr>
  `).join('');

  bodyEl.innerHTML = `
    <div class="bindings-wrap">
      <div class="bindings-note">${escapeHtml(t('settings.bindingsNote'))}</div>
      <table class="bindings-table">
        <thead>
          <tr>
            <th class="action-cell"></th>
            <th class="binding-cell">${escapeHtml(t('settings.kbm'))}</th>
            <th class="binding-cell">${escapeHtml(vendorLabel)}</th>
          </tr>
        </thead>
        <tbody>${rowMarkup}</tbody>
      </table>
    </div>
  `;
}

function buildGraphicsBody(bodyEl: HTMLElement, settings: GraphicsSettings, items: MenuItem[]): void {
  const activePreset = findActivePreset(settings);
  bodyEl.innerHTML = `
    <div class="rows">
      ${renderPresetRow(activePreset)}
      <div class="preset-quip"></div>
      ${renderDisplayRow()}
      ${renderResolutionRow(settings)}
      ${renderToggleRow('aa',    t('settings.antiAliasing'),      settings.antiAliasing)}
      ${renderToggleRow('cl',    t('settings.cinematicLighting'), settings.cinematicLighting)}
      ${renderShadowsRow(settings)}
      ${renderToggleRow('bloom', t('settings.bloom'),              settings.bloom)}
      ${renderToggleRow('cf',    t('settings.cinematicFlares'),   settings.cinematicFlares)}
      ${renderToggleRow('mb',    t('settings.motionBlur'),        settings.motionBlur)}
      ${renderToggleRow('cc',    t('settings.cinematicColor'),    settings.cinematicColor)}
      ${renderToggleRow('ao',    t('settings.ambientOcclusion'),  settings.ambientOcclusion)}
    </div>
  `;

  let row = 1;
  wirePresetButtons(bodyEl, settings, items, row++);
  wireDisplaySegments(bodyEl, items, row++);
  wireResolutionSegments(bodyEl, settings, items, row++);
  wireToggleSegments(bodyEl, 'aa',    settings, items, row++, (s, v) => { s.antiAliasing = v; });
  wireToggleSegments(bodyEl, 'cl',    settings, items, row++, (s, v) => { s.cinematicLighting = v; });
  wireShadowsSegments(bodyEl, settings, items, row++);
  wireToggleSegments(bodyEl, 'bloom', settings, items, row++, (s, v) => { s.bloom = v; });
  wireToggleSegments(bodyEl, 'cf',    settings, items, row++, (s, v) => { s.cinematicFlares = v; });
  wireToggleSegments(bodyEl, 'mb',    settings, items, row++, (s, v) => { s.motionBlur = v; });
  wireToggleSegments(bodyEl, 'cc',    settings, items, row++, (s, v) => { s.cinematicColor = v; });
  wireToggleSegments(bodyEl, 'ao',    settings, items, row++, (s, v) => { s.ambientOcclusion = v; });

  updateResolutionLabels(bodyEl);
}

function renderPresetRow(activePreset: GraphicsPresetId | null): string {
  const btn = (id: GraphicsPresetId): string =>
    `<button type="button" class="preset-btn" data-preset="${id}"${activePreset === id ? ' data-active=""' : ''}>${escapeHtml(t(`settings.preset.${id}`))}</button>`;
  return `
    <div class="row preset-row" data-row="presets">
      <div class="row-label">${escapeHtml(t('settings.preset.label'))}</div>
      <div class="preset-btns">
        ${GRAPHICS_PRESET_ORDER.map(btn).join('')}
      </div>
    </div>
  `;
}

function wirePresetButtons(bodyEl: HTMLElement, settings: GraphicsSettings, items: MenuItem[], row: number): void {
  const btns = bodyEl.querySelectorAll<HTMLButtonElement>('.preset-btn');
  const quipEl = bodyEl.querySelector('.preset-quip') as HTMLElement;
  let col = 0;
  btns.forEach((btn) => {
    const id = btn.dataset.preset as GraphicsPresetId;
    items.push({
      el: btn,
      row, col: col++,
      onFocus: () => { quipEl.textContent = t(`settings.preset.${id}.quip`); },
      onActivate: () => {
        Object.assign(settings, GRAPHICS_PRESETS[id]);
        for (const b of Array.from(btns)) {
          if (b === btn) b.setAttribute('data-active', '');
          else b.removeAttribute('data-active');
        }
        applyAndSaveGraphics(settings);
        // The individual rows below are now stale — re-render the
        // graphics body so segmented states match the new settings.
        // Mouse hover won't refire onFocus, so quip stays sticky until
        // the user moves focus.
        rerenderGraphicsBody(bodyEl, settings);
      }
    });
  });
  // Seed the quip with the active preset's text so the area isn't blank
  // before the user moves focus to a preset.
  const initial = findActivePreset(settings);
  if (initial) quipEl.textContent = t(`settings.preset.${initial}.quip`);
  else quipEl.textContent = t('settings.preset.custom.quip');
}

/** Repaint just the rows that depend on the current settings (everything
 *  except the preset row + quip — those are stable across a preset
 *  click). Used after a preset is applied so segmented rows reflect the
 *  new state without rebuilding the entire MenuNav. The simplest
 *  correct path is a full rebuild of the graphics body from the parent
 *  modal — but the parent owns the items array and the nav, so instead
 *  we just refresh the segmented `data-active` attributes in place. */
function rerenderGraphicsBody(bodyEl: HTMLElement, settings: GraphicsSettings): void {
  const setActiveByValue = (group: string, value: string): void => {
    const segs = bodyEl.querySelectorAll<HTMLButtonElement>(`[data-segments="${group}"] .seg`);
    for (const seg of Array.from(segs)) {
      if (seg.dataset.value === value) seg.setAttribute('data-active', '');
      else seg.removeAttribute('data-active');
    }
  };
  setActiveByValue('resolution', String(settings.renderScale));
  setActiveByValue('aa',    settings.antiAliasing ? 'on' : 'off');
  setActiveByValue('cl',    settings.cinematicLighting ? 'on' : 'off');
  setActiveByValue('shadows', settings.shadows);
  setActiveByValue('bloom', settings.bloom ? 'on' : 'off');
  setActiveByValue('cf',    settings.cinematicFlares ? 'on' : 'off');
  setActiveByValue('mb',    settings.motionBlur ? 'on' : 'off');
  setActiveByValue('cc',    settings.cinematicColor ? 'on' : 'off');
  setActiveByValue('ao',    settings.ambientOcclusion ? 'on' : 'off');
}

// ---------------------------------------------------------------------------
// Field renderers

function renderDisplayRow(): string {
  const fs = isFullscreen();
  const seg = (val: 'windowed' | 'fullscreen', label: string, active: boolean): string =>
    `<button type="button" class="seg" data-value="${val}"${active ? ' data-active=""' : ''}>${escapeHtml(label)}</button>`;
  return `
    <div class="row" data-row="display">
      <div class="row-label">${escapeHtml(t('settings.display'))}</div>
      <div class="segments" data-segments="display">
        ${seg('windowed',   t('settings.windowed'),   !fs)}
        ${seg('fullscreen', t('settings.fullscreen'), fs)}
      </div>
    </div>
  `;
}

function wireDisplaySegments(bodyEl: HTMLElement, items: MenuItem[], row: number): void {
  const segs = bodyEl.querySelectorAll<HTMLButtonElement>('[data-segments="display"] .seg');
  let col = 0;
  segs.forEach(seg => {
    const value = seg.dataset.value as 'windowed' | 'fullscreen';
    items.push({
      el: seg, row, col: col++,
      onActivate: () => {
        // requestFullscreen needs a user activation. Click / Enter
        // satisfy this; gamepad input does not, so the call may reject —
        // enterFullscreen() swallows the error. The fullscreenchange
        // listener (registered in showSettingsModal) is the source of
        // truth for the active-segment state, so we don't optimistically
        // mark active here.
        if (value === 'fullscreen') void enterFullscreen();
        else void exitFullscreen();
      }
    });
  });
}

/** Refresh the display row's active segment from the live browser state.
 *  Called from a fullscreenchange listener so Esc-to-exit (or the OS
 *  pressing exit) updates the UI without the user re-opening the tab. */
function refreshDisplayRow(bodyEl: HTMLElement): void {
  const fs = isFullscreen();
  const segs = bodyEl.querySelectorAll<HTMLButtonElement>('[data-segments="display"] .seg');
  for (const s of Array.from(segs)) {
    const wantActive = (s.dataset.value === 'fullscreen') === fs;
    if (wantActive) s.setAttribute('data-active', '');
    else s.removeAttribute('data-active');
  }
}

function renderResolutionRow(settings: GraphicsSettings): string {
  const seg = (val: RenderScale): string =>
    `<button type="button" class="seg" data-value="${val}"${settings.renderScale === val ? ' data-active=""' : ''}><span class="seg-text" data-scale="${val}"></span></button>`;
  return `
    <div class="row" data-row="resolution">
      <div class="row-label">${escapeHtml(t('settings.resolution'))}</div>
      <div class="segments" data-segments="resolution">
        ${seg(1)}${seg(0.75)}${seg(0.5)}${seg(0.25)}${seg(0.1)}
      </div>
    </div>
  `;
}

function renderToggleRow(key: string, label: string, on: boolean): string {
  return `
    <div class="row" data-row="${key}">
      <div class="row-label">${escapeHtml(label)}</div>
      <div class="segments" data-segments="${key}">
        <button type="button" class="seg" data-value="off"${!on ? ' data-active=""' : ''}>${escapeHtml(t('settings.off'))}</button>
        <button type="button" class="seg" data-value="on"${on ? ' data-active=""' : ''}>${escapeHtml(t('settings.on'))}</button>
      </div>
    </div>
  `;
}

function renderShadowsRow(settings: GraphicsSettings): string {
  const opts: ShadowQuality[] = ['off', 'low', 'medium', 'high'];
  const labels: Record<ShadowQuality, string> = {
    off: t('settings.off'),
    low: t('settings.low'),
    medium: t('settings.medium'),
    high: t('settings.high')
  };
  const segs = opts.map(o =>
    `<button type="button" class="seg" data-value="${o}"${settings.shadows === o ? ' data-active=""' : ''}>${escapeHtml(labels[o])}</button>`
  ).join('');
  return `
    <div class="row" data-row="shadows">
      <div class="row-label">${escapeHtml(t('settings.shadows'))}</div>
      <div class="segments" data-segments="shadows">${segs}</div>
    </div>
  `;
}

function renderSliderRow(
  key: string, label: string, value: number, min: number, max: number, step: number,
  display: string
): string {
  return `
    <div class="row slider-row" data-row="${key}">
      <div class="row-label">${escapeHtml(label)}</div>
      <div class="slider-stack">
        <input type="range" min="${min}" max="${max}" step="${step}" value="${value}" />
        <div class="slider-value">${escapeHtml(display)}</div>
      </div>
    </div>
  `;
}

function wireResolutionSegments(bodyEl: HTMLElement, settings: GraphicsSettings, items: MenuItem[], row: number): void {
  const segs = bodyEl.querySelectorAll<HTMLButtonElement>('[data-segments="resolution"] .seg');
  let col = 0;
  segs.forEach(seg => {
    const value = parseFloat(seg.dataset.value!) as RenderScale;
    items.push({
      el: seg, row, col: col++,
      onActivate: () => {
        settings.renderScale = value;
        markActive(seg);
        applyAndSaveGraphics(settings);
      }
    });
  });
}

function wireToggleSegments(bodyEl: HTMLElement, key: string, settings: GraphicsSettings, items: MenuItem[], row: number, write: (s: GraphicsSettings, v: boolean) => void): void {
  const segs = bodyEl.querySelectorAll<HTMLButtonElement>(`[data-segments="${key}"] .seg`);
  let col = 0;
  segs.forEach(seg => {
    const v = seg.dataset.value === 'on';
    items.push({
      el: seg, row, col: col++,
      onActivate: () => {
        write(settings, v);
        markActive(seg);
        applyAndSaveGraphics(settings);
      }
    });
  });
}

function wireShadowsSegments(bodyEl: HTMLElement, settings: GraphicsSettings, items: MenuItem[], row: number): void {
  const segs = bodyEl.querySelectorAll<HTMLButtonElement>('[data-segments="shadows"] .seg');
  let col = 0;
  segs.forEach(seg => {
    const v = seg.dataset.value as ShadowQuality;
    items.push({
      el: seg, row, col: col++,
      onActivate: () => {
        settings.shadows = v;
        markActive(seg);
        applyAndSaveGraphics(settings);
      }
    });
  });
}

function markActive(seg: HTMLElement): void {
  const group = seg.parentElement;
  if (!group) return;
  for (const s of Array.from(group.querySelectorAll<HTMLElement>('.seg'))) {
    if (s === seg) s.setAttribute('data-active', '');
    else s.removeAttribute('data-active');
  }
}

function applyAndSaveGraphics(settings: GraphicsSettings): void {
  applyGraphicsSettingsToConfig(settings);
  saveGraphicsSettings(settings);
  // Re-evaluate which preset (if any) is now active and refresh the
  // preset-button highlights + the quip line accordingly. A hand-tweak
  // typically lands on no preset, but going from 'medium' → 'low' via
  // individual rows could land on 'low' exactly — and we want the
  // 'low' button highlighted in that case.
  refreshPresetHighlight(settings);
}

function refreshPresetHighlight(settings: GraphicsSettings): void {
  // Active modal can have at most one .preset-btns row in the DOM.
  const btns = document.querySelectorAll<HTMLButtonElement>('.settings-modal .preset-btn');
  if (btns.length === 0) return;
  const active = findActivePreset(settings);
  for (const btn of Array.from(btns)) {
    const id = btn.dataset.preset as GraphicsPresetId;
    if (id === active) btn.setAttribute('data-active', '');
    else btn.removeAttribute('data-active');
  }
  const quipEl = document.querySelector('.settings-modal .preset-quip') as HTMLElement | null;
  if (quipEl) {
    quipEl.textContent = active
      ? t(`settings.preset.${active}.quip`)
      : t('settings.preset.custom.quip');
  }
}

function updateResolutionLabels(bodyEl: HTMLElement): void {
  const w = window.innerWidth * window.devicePixelRatio;
  const h = window.innerHeight * window.devicePixelRatio;
  for (const span of Array.from(bodyEl.querySelectorAll<HTMLElement>('[data-scale]'))) {
    const scale = parseFloat(span.dataset.scale!);
    span.textContent = `${Math.round(w * scale)} × ${Math.round(h * scale)}`;
  }
}

/** Bump a range input's value by `delta * step`, clamp to its [min, max],
 *  and dispatch an 'input' event so any per-input listener (settings
 *  apply / save / display update) runs as if the user dragged the thumb.
 *  Used by MenuItem.onAdjust to drive sliders from controller dpad /
 *  left-stick — keyboard ArrowLeft/Right on the focused input is handled
 *  natively by the browser. */
function adjustSlider(input: HTMLInputElement, delta: -1 | 1, step: number): void {
  const min = parseFloat(input.min);
  const max = parseFloat(input.max);
  const cur = parseFloat(input.value);
  const next = Math.max(min, Math.min(max, cur + delta * step));
  // Round to the slider's native step grid so display precision stays
  // consistent (e.g. 0.05 → "5%" cleanly).
  const stepGrid = parseFloat(input.step) || step;
  const snapped = Math.round(next / stepGrid) * stepGrid;
  const clamped = Math.max(min, Math.min(max, snapped));
  input.value = String(clamped);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

function formatSensitivity(v: number): string {
  return `${v.toFixed(2)}×`;
}

function formatPercent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function confirmAndResetTutorial(): void {
  showConfirmationPopup({
    title: t('settings.resetTutorialConfirmTitle'),
    body: t('settings.resetTutorialConfirmBody'),
    confirmLabel: t('settings.resetTutorialConfirmAction'),
    // Reset is non-destructive (it just queues the tutorial to replay
    // next launch) — confirm button stays standard primary blue.
    destructive: false,
    onConfirm: () => { resetTutorialState(); }
  });
}

function confirmAndSkipTutorial(
  flow: { requestSkip: () => void },
  dismissModal: () => void
): void {
  showConfirmationPopup({
    title: t('settings.skipTutorialConfirmTitle'),
    body: t('settings.skipTutorialConfirmBody'),
    confirmLabel: t('settings.skipTutorialConfirmAction'),
    destructive: true,
    onConfirm: () => {
      flow.requestSkip();
      // Dismiss the settings modal so the tutorial teardown isn't
      // sandwiched under a paused dialog.
      dismissModal();
    }
  });
}

function confirmAndLeaveMatch(
  onLeaveMatch: () => void,
  dismissModal: () => void
): void {
  showConfirmationPopup({
    title: t('settings.leaveMatchConfirmTitle'),
    body: t('settings.leaveMatchConfirmBody'),
    confirmLabel: t('settings.leaveMatchConfirmAction'),
    destructive: true,
    onConfirm: () => {
      onLeaveMatch();
      dismissModal();
    }
  });
}

function releasePointerLock(): void {
  if (document.pointerLockElement) {
    try { document.exitPointerLock?.(); } catch { /* ignore */ }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

// Reference CONFIG so tree-shaking doesn't drop the import — settings
// values flow through CONFIG's mutable struct (via applyControlsSettings
// / applyAudioSettings) before reaching the consumers, even though this
// file doesn't read CONFIG directly.
void CONFIG;

// ---------------------------------------------------------------------------
// Styles

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = STYLES_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

/* Sit above the home screen (#home-screen z:100) and any in-match HUD. */
.settings-modal {
  position: fixed; inset: 0; z-index: 110;
  pointer-events: auto;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
}
.settings-modal-backdrop {
  position: absolute; inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  opacity: 0;
  transition: opacity 200ms ease-out;
}
.settings-modal.shown .settings-modal-backdrop { opacity: 1; }
.settings-modal.closing .settings-modal-backdrop { opacity: 0; transition-duration: 220ms; }

.settings-modal-stage-host {
  position: absolute; inset: 0;
  pointer-events: none;
}
.settings-modal-card {
  position: absolute; top: 50%; left: 50%;
  transform-origin: center center;
  /* Width / height set inline by JS; StageScaler writes the transform. */
  pointer-events: auto;
  background: linear-gradient(180deg, #C7E6F1 0%, #B8DEEC 100%);
  border-radius: 28px;
  box-shadow: 0 16px 24px rgba(0,0,0,0.22), inset 0 -4px 6px rgba(89, 168, 192, 0.4);
  display: flex; flex-direction: column;
  overflow: hidden;
  opacity: 0;
  transition: opacity 240ms cubic-bezier(0.2, 0.9, 0.2, 1);
}
.settings-modal.shown .settings-modal-card { opacity: 1; }
.settings-modal.closing .settings-modal-card { opacity: 0; transition-duration: 200ms; }

/* Header — title + close button. */
.settings-modal-header {
  display: flex; align-items: center; gap: 16px;
  padding: 26px 32px 12px 36px;
  flex-shrink: 0;
}
.settings-modal-header .header-title {
  flex: 1;
  font-size: 48px; line-height: 1;
  color: #0E3C4A;
  letter-spacing: 0.04em;
}
.settings-modal .close-btn {
  width: 60px; height: 60px;
  border: 0; cursor: pointer;
  background: linear-gradient(180deg, #F1F9FB 0%, #C3D6DB 100%);
  box-shadow: 0 4px 0 #ADC5CC;
  border-radius: 14px;
  font-family: 'Chewy', sans-serif;
  font-size: 36px; line-height: 1;
  color: #0E3C4A;
  display: flex; align-items: center; justify-content: center;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
  flex-shrink: 0;
}
.settings-modal .close-btn:active { transform: translateY(3px); box-shadow: 0 1px 0 #ADC5CC; }
.settings-modal .close-btn:hover,
.settings-modal .close-btn.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; }

/* Tabs — pill-shaped row above the body. */
.settings-modal-tabs {
  display: flex; gap: 10px;
  padding: 0 36px 14px 36px;
  flex-shrink: 0;
}
.settings-modal .tab-btn {
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 22px; line-height: 1;
  padding: 12px 22px;
  background: rgba(255,255,255,0.5);
  color: #6CABC1;
  border-radius: 18px;
  letter-spacing: 0.04em;
  transition: background 140ms ease, color 140ms ease, filter 140ms ease;
}
.settings-modal .tab-btn:hover { color: #0E3C4A; }
.settings-modal .tab-btn.active {
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  color: #FFFFFF;
  -webkit-text-stroke: 2px #2A6DA0;
  paint-order: stroke fill;
  box-shadow: 0 4px 0 #2C7AB6;
}
.settings-modal .tab-btn.focused { outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; }

/* Body — scrollable region, holds rows or the bindings table. */
.settings-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 36px 28px 36px;
  scrollbar-width: thin;
  scrollbar-color: rgba(14,60,74,0.25) transparent;
}
.settings-modal-body::-webkit-scrollbar { width: 8px; }
.settings-modal-body::-webkit-scrollbar-thumb { background: rgba(14,60,74,0.22); border-radius: 4px; }

/* Row layout — used by every field type (segmented, slider, action). */
.settings-modal .rows { display: flex; flex-direction: column; gap: 12px; }
.settings-modal .row {
  background: #FFFFFF;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 -3px 0 rgba(176, 209, 219, 0.6);
  padding: 14px 22px;
  display: grid;
  grid-template-columns: 280px 1fr;
  align-items: center;
  gap: 18px;
}
.settings-modal .row-label {
  font-size: 22px; line-height: 1;
  color: #6CABC1;
  letter-spacing: 0.04em;
}

/* Segmented (resolution / shadows / on-off toggle). */
.settings-modal .segments {
  display: flex; gap: 6px;
  background: linear-gradient(180deg, #DDF1F7 0%, #C7E0E9 100%);
  padding: 4px;
  border-radius: 12px;
  box-shadow: inset 0 2px 3px rgba(89, 168, 192, 0.4);
}
.settings-modal .seg {
  flex: 1;
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 18px; line-height: 1;
  padding: 10px 12px;
  background: transparent;
  color: #6CABC1;
  border-radius: 9px;
  letter-spacing: 0.03em;
  white-space: nowrap;
  transition: background 140ms ease, color 140ms ease, box-shadow 140ms ease;
}
.settings-modal .seg:hover { color: #0E3C4A; }
.settings-modal .seg[data-active] {
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  color: #FFFFFF;
  -webkit-text-stroke: 2px #2A6DA0;
  paint-order: stroke fill;
  box-shadow: 0 3px 0 #2C7AB6;
}
.settings-modal .seg.focused { outline: 3px solid rgba(255,255,255,0.7); outline-offset: -3px; }

/* Slider field. */
.settings-modal .slider-stack {
  display: flex; align-items: center; gap: 14px;
}
.settings-modal .slider-stack input[type=range] {
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  height: 14px;
  background: linear-gradient(180deg, #DDF1F7 0%, #C7E0E9 100%);
  border-radius: 7px;
  outline: 0;
  cursor: pointer;
  box-shadow: inset 0 2px 3px rgba(89, 168, 192, 0.4);
  margin: 0;
}
.settings-modal .slider-stack input[type=range]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 28px; height: 28px;
  border-radius: 50%;
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  border: 3px solid #FFF;
  box-shadow: 0 3px 0 #2C7AB6, 0 2px 4px rgba(0,0,0,0.2);
  cursor: grab;
}
.settings-modal .slider-stack input[type=range]:active::-webkit-slider-thumb { cursor: grabbing; }
.settings-modal .slider-stack input[type=range]::-moz-range-thumb {
  width: 28px; height: 28px;
  border-radius: 50%;
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  border: 3px solid #FFF;
  box-shadow: 0 3px 0 #2C7AB6, 0 2px 4px rgba(0,0,0,0.2);
  cursor: grab;
}
.settings-modal .slider-row.focused { outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; outline-style: solid; border-radius: 18px; }
.settings-modal .slider-stack .slider-value {
  min-width: 70px;
  text-align: right;
  font-size: 26px; line-height: 1;
  color: #0E3C4A;
  letter-spacing: 0.04em;
}

/* Action-row buttons (Reset Tutorial / Skip Tutorial). */
.settings-modal .action-row {
  border: 0; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Chewy', sans-serif;
  font-size: 28px; line-height: 1;
  padding: 16px 22px;
  border-radius: 18px;
  letter-spacing: 0.04em;
  transition: transform 140ms ease, box-shadow 140ms ease, filter 140ms ease;
  color: #FFFFFF;
}
.settings-modal .action-row.destructive {
  background: linear-gradient(180deg, #FFAA66 0%, #FF6E2D 100%);
  -webkit-text-stroke: 2px #7A2A0E;
  paint-order: stroke fill;
  box-shadow: 0 4px 0 #B14400, 0 6px 14px rgba(0,0,0,0.18);
}
.settings-modal .action-row.primary {
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  -webkit-text-stroke: 2px #2A6DA0;
  paint-order: stroke fill;
  box-shadow: 0 4px 0 #2C7AB6, 0 6px 14px rgba(0,0,0,0.2);
}
.settings-modal .action-row:active { transform: translateY(3px); }
.settings-modal .action-row.destructive:active { box-shadow: 0 1px 0 #B14400; }
.settings-modal .action-row.primary:active { box-shadow: 0 1px 0 #2C7AB6; }
.settings-modal .action-row:hover,
.settings-modal .action-row.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.6); outline-offset: 3px; }

/* Bindings table — controls tab. */
.settings-modal .bindings-wrap {
  background: #FFFFFF;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 -3px 0 rgba(176, 209, 219, 0.6);
  padding: 22px 28px;
}
.settings-modal .bindings-note {
  font-size: 18px; line-height: 1.3;
  color: #6CABC1;
  margin-bottom: 16px;
  letter-spacing: 0.02em;
}
.settings-modal .bindings-table {
  width: 100%;
  border-collapse: collapse;
}
.settings-modal .bindings-table th {
  text-align: left;
  font-size: 18px; line-height: 1;
  color: #6CABC1;
  letter-spacing: 0.04em;
  padding: 8px 12px;
  border-bottom: 2px solid rgba(108, 171, 193, 0.3);
}
.settings-modal .bindings-table td {
  font-size: 22px; line-height: 1;
  color: #0E3C4A;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(108, 171, 193, 0.18);
}
.settings-modal .bindings-table tr:last-child td { border-bottom: 0; }
.settings-modal .bindings-table .action-cell { width: 40%; color: #5C8090; }
.settings-modal .bindings-table .binding-cell { width: 30%; }

/* Resolution sub-label tweaks — the "1280 × 720" inside the seg. */
.settings-modal .seg .seg-text { display: inline-block; font-size: 14px; line-height: 1; }

/* Graphics preset row — five named buckets the user picks from. Buttons
   are square pills with a subtle blue gradient in their resting state
   and the same primary-blue active treatment as segments. */
.settings-modal .preset-row .preset-btns {
  display: flex; gap: 8px;
  flex-wrap: wrap;
}
.settings-modal .preset-btn {
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 18px; line-height: 1;
  padding: 12px 14px;
  border-radius: 12px;
  letter-spacing: 0.03em;
  background: linear-gradient(180deg, #DDF1F7 0%, #C7E0E9 100%);
  color: #6CABC1;
  box-shadow: 0 3px 0 rgba(89, 168, 192, 0.4);
  transition: background 140ms ease, color 140ms ease, box-shadow 140ms ease, filter 140ms ease;
}
.settings-modal .preset-btn:hover { color: #0E3C4A; filter: brightness(1.04); }
.settings-modal .preset-btn[data-active] {
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  color: #FFFFFF;
  -webkit-text-stroke: 2px #2A6DA0;
  paint-order: stroke fill;
  box-shadow: 0 3px 0 #2C7AB6;
}
.settings-modal .preset-btn.focused { outline: 3px solid rgba(255,255,255,0.7); outline-offset: 3px; }

/* Quip line shown beneath the preset row. Sits just below the white
   pill so it doesn't fight the row layout — italicised secondary text. */
.settings-modal .preset-quip {
  font-size: 16px;
  line-height: 1.3;
  color: #5C8090;
  font-style: italic;
  letter-spacing: 0.02em;
  padding: 4px 6px 6px 6px;
  min-height: 22px;
}

/* Language row — special row inside Gameplay tab. Looks like a regular
   row but the right-hand column shows flag + name + chevron and the
   whole pill is one focusable button (cursor moves to a sub-panel of
   flag tiles when activated). */
.settings-modal .language-row {
  border: 0; cursor: pointer;
  text-align: left;
  width: 100%;
  font-family: 'Chewy', sans-serif;
  display: grid;
  grid-template-columns: 280px 1fr;
  align-items: center;
  gap: 18px;
  background: #FFFFFF;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 -3px 0 rgba(176, 209, 219, 0.6);
  padding: 14px 22px;
  transition: filter 120ms ease;
}
.settings-modal .language-row:hover,
.settings-modal .language-row.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; }
.settings-modal .language-current {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 14px;
  font-size: 26px;
  color: #0E3C4A;
  letter-spacing: 0.04em;
}
.settings-modal .language-flag { font-size: 32px; line-height: 1; }
.settings-modal .language-name { font-size: 22px; }
.settings-modal .language-chevron { font-size: 32px; color: #6CABC1; line-height: 1; }

/* Language sub-panel — replaces the Gameplay body when active. Back
   button at the top, vertical scrollable flag list below. */
.settings-modal .lang-subpanel {
  display: flex; flex-direction: column;
  gap: 12px;
  height: 100%;
}
.settings-modal .lang-back {
  align-self: flex-start;
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 22px; line-height: 1;
  padding: 12px 18px 12px 12px;
  background: linear-gradient(180deg, #F1F9FB 0%, #C3D6DB 100%);
  box-shadow: 0 4px 0 #ADC5CC;
  border-radius: 14px;
  color: #0E3C4A;
  display: flex; align-items: center; gap: 8px;
  letter-spacing: 0.04em;
  transition: filter 120ms ease, transform 120ms ease, box-shadow 120ms ease;
}
.settings-modal .lang-back:active { transform: translateY(3px); box-shadow: 0 1px 0 #ADC5CC; }
.settings-modal .lang-back:hover,
.settings-modal .lang-back.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; }
.settings-modal .lang-back-chevron { font-size: 32px; line-height: 1; }
.settings-modal .lang-grid {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex; flex-direction: column;
  gap: 8px;
  padding: 4px 4px 4px 0;
}
.settings-modal .lang-flag {
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 24px;
  padding: 12px 18px;
  background: #FFFFFF;
  border-radius: 14px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12), inset 0 -3px 0 rgba(176, 209, 219, 0.45);
  color: #0E3C4A;
  display: flex; align-items: center; gap: 18px;
  text-align: left;
  letter-spacing: 0.02em;
  transition: filter 120ms ease;
}
.settings-modal .lang-flag-emoji { font-size: 32px; line-height: 1; flex-shrink: 0; }
.settings-modal .lang-flag-name { flex: 1; }
.settings-modal .lang-flag.selected {
  background: linear-gradient(180deg, #DDF1F7 0%, #C7E0E9 100%);
  box-shadow: 0 2px 6px rgba(0,0,0,0.12), inset 0 -3px 0 rgba(89, 168, 192, 0.45), inset 0 0 0 3px #6FCEEB;
}
.settings-modal .lang-flag:hover,
.settings-modal .lang-flag.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; }
[dir="rtl"] .settings-modal .lang-flag-name { direction: rtl; }

/* Account sub-panel — stacks the same .row pills as Gameplay but the right
   column hosts richer content (multi-line stacks, an input, action rows). */
.settings-modal .account-subpanel .acc-rows {
  display: flex; flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 4px 4px 0;
}
.settings-modal .acc-name-stack { display: flex; align-items: center; gap: 14px; }
.settings-modal .acc-name {
  flex: 1;
  font-size: 28px; line-height: 1;
  color: #0E3C4A;
  letter-spacing: 0.04em;
}
.settings-modal .acc-rename {
  font-size: 18px;
  padding: 10px 16px;
  flex-shrink: 0;
}
.settings-modal .acc-token-stack {
  display: flex; flex-direction: column;
  gap: 10px;
}
.settings-modal .acc-token-blurb {
  font-size: 16px; line-height: 1.3;
  color: #5C8090;
  letter-spacing: 0.02em;
}
.settings-modal .acc-token-input,
.settings-modal .acc-import-input {
  width: 100%;
  border: 0; outline: 0;
  background: linear-gradient(180deg, #DDF1F7 0%, #C7E0E9 100%);
  font-family: 'Chewy', sans-serif;
  font-size: 18px; line-height: 1;
  color: #0E3C4A;
  padding: 10px 14px;
  border-radius: 12px;
  box-shadow: inset 0 2px 3px rgba(89, 168, 192, 0.4);
  letter-spacing: 0.04em;
  box-sizing: border-box;
}
.settings-modal .acc-token-actions {
  display: flex; gap: 8px;
  flex-wrap: wrap;
}
.settings-modal .acc-token-actions .action-row {
  flex: 0 0 auto;
  font-size: 18px;
  padding: 10px 16px;
}
.settings-modal .acc-import-status {
  min-height: 22px;
  font-size: 16px; line-height: 1.2;
  letter-spacing: 0.02em;
}
.settings-modal .acc-import-status[data-state="checking"] { color: #5C8090; font-style: italic; }
.settings-modal .acc-import-status[data-state="error"]    { color: #C24A2C; }
.settings-modal .acc-token-row .row-label,
.settings-modal .acc-import-row .row-label { align-self: flex-start; padding-top: 6px; }
.settings-modal .acc-import-row .acc-apply { align-self: flex-start; font-size: 18px; padding: 10px 16px; }
`;
